import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DialogService } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { CampaignsService } from '../../../../../../services/api/generated/campaigns/campaigns.service';
import { ModalHelperService } from '../../../../../../services/modal-helper.service';
import { ToastService } from '../../../../../../services/toast.service';
import { CampaignStore } from '../../../../../../stores/campaign.store';
import { Campaign, CampaignStatus } from '../../../../../../types/campaign.interface';
import { ConflictResolution, MergeAnalysis, toMergeResolutionEntry } from '../../../../../../types/campaign-merge.interface';
import { isConflict } from '../../../../../../utils/http-error.utils';
import { CampaignMergeDialogComponent, MergeDialogResult } from '../dialogs/campaign-merge-dialog.component';
import {
  CampaignConflictDialogComponent,
  type ConflictDialogData,
  ConflictDialogResult,
} from '../dialogs/campaign-conflict-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class CampaignMergeService {
  private readonly dialogService = inject(DialogService);
  private readonly modalHelper = inject(ModalHelperService);
  private readonly campaignsApi = inject(CampaignsService);
  private readonly campaignStore = inject(CampaignStore);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslocoService);

  openMergeDialog(assetId: number): void {
    const ongoingCount = this.campaignStore
      .entities()
      .filter((c: Campaign) => c.assetId === assetId && c.status === CampaignStatus.ON_GOING).length;

    if (ongoingCount < 2) {
      this.toastService.info('entities.campaign.merge.notEnoughCampaigns');
      return;
    }

    const ref = this.dialogService.open(
      CampaignMergeDialogComponent,
      this.modalHelper.getFormConfig({
        header: this.translate.translate('entities.campaign.merge.dialogTitle'),
        closeOnEscape: true,
        data: { assetId },
      }),
    );

    ref.onClose.subscribe((result?: MergeDialogResult) => {
      if (result) {
        void this.analyzeThenMerge(result.campaignA, result.campaignB);
      }
    });
  }

  private async analyzeThenMerge(campaignA: Campaign, campaignB: Campaign): Promise<void> {
    try {
      // MergeConflict.entityType narrows the wire `string` to a UI union; shapes remain compatible.
      const response = await firstValueFrom(
        this.campaignsApi.postCampaignMergePreviewResource({
          campaignAId: campaignA.id,
          campaignBId: campaignB.id,
        }),
      );
      const analysis = response as unknown as MergeAnalysis;

      if (!analysis.hasConflicts) {
        await this.executeMerge(campaignA.id, campaignB.id);
        return;
      }

      const ref = this.dialogService.open(
        CampaignConflictDialogComponent,
        this.modalHelper.getMediumConfig({
          header: this.translate.translate('entities.campaign.merge.conflictsTitle'),
          data: {
            analysis,
            campaignA,
            campaignB,
            mode: 'merge',
          } satisfies ConflictDialogData,
        }),
      );

      ref.onClose.subscribe((result?: ConflictDialogResult) => {
        if (result) {
          void this.executeMerge(campaignA.id, campaignB.id, result.resolutions);
        }
      });
    } catch (err) {
      // HttpErrorInterceptor already raises a localized toast for the 409 timestamp
      // collision; suppress the generic one here to avoid a duplicate notification.
      if (!isConflict(err)) {
        this.toastService.error('entities.campaign.merge.mergeError');
      }
    }
  }

  private async executeMerge(campaignAId: number, campaignBId: number, resolutions?: ConflictResolution[]): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.campaignsApi.postCampaignMergeResource({
          campaignAId,
          campaignBId,
          ...(resolutions !== undefined && { resolutions: resolutions.map(toMergeResolutionEntry) }),
        }),
      );

      const assetId = response.campaign.assetId;
      await this.campaignStore.getByAssetId(assetId);
      this.toastService.success('entities.campaign.merge.mergeSuccess');
    } catch (err) {
      // HttpErrorInterceptor already raises a localized toast for the 409 timestamp
      // collision; suppress the generic one here to avoid a duplicate notification.
      if (!isConflict(err)) {
        this.toastService.error('entities.campaign.merge.mergeError');
      }
    }
  }
}
